import type { ExtracaoFaturaCrmResposta } from '../../../types';

// O n8n extrai o tipo de fornecimento como aparece na fatura (ex: "MONOFÁSICO"),
// mas os formulários de UC esperam exatamente 'Monofásico' | 'Bifásico' | 'Trifásico'.
export const normalizeTipoLigacao = (raw?: string): 'Monofásico' | 'Bifásico' | 'Trifásico' | undefined => {
    if (!raw) return undefined;
    const upper = raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
    if (upper.includes('MONO')) return 'Monofásico';
    if (upper.includes('BIF')) return 'Bifásico';
    if (upper.includes('TRIF')) return 'Trifásico';
    return undefined;
};

export interface FaturaExtractedUC {
    numero_uc?: string;
    num_medidor?: string;
    concessionaria?: string;
    tipo_ligacao?: 'Monofásico' | 'Bifásico' | 'Trifásico';
    grupo_tarifario?: string;
    consumo_medio_kwh?: number;
    valor_tarifa?: number;
    titular_fatura?: string;
    cpf_cnpj_titular?: string;
    cep?: string;
    logradouro?: string;
    numero?: string;
    complemento?: string;
    quadra?: string;
    lote?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
}

/** Converte o payload `data` do webhook `crm_fatura_upload_webhook_url` nos campos de uma UnidadeConsumidora/UCData. */
export function mapFaturaDataToUC(data: Record<string, any>): FaturaExtractedUC {
    return {
        numero_uc: data.numero_uc,
        num_medidor: data.num_medidor,
        concessionaria: data.concessionaria,
        tipo_ligacao: normalizeTipoLigacao(data.tipo_ligacao),
        grupo_tarifario: data.grupo_tarifario || undefined,
        consumo_medio_kwh: data.consumo_medio_kwh ? Number(data.consumo_medio_kwh) : undefined,
        valor_tarifa: data.valor_tarifa ? Number(data.valor_tarifa) : undefined,
        titular_fatura: data.titular_fatura,
        cpf_cnpj_titular: data.cpf_cnpj_titular,
        cep: data.cep,
        logradouro: data.logradouro,
        numero: data.numero,
        complemento: data.complemento,
        quadra: data.quadra,
        lote: data.lote,
        bairro: data.bairro,
        cidade: data.cidade,
        estado: data.estado,
    };
}

// O parser aceita tanto ',' quanto '.' como separador decimal no histórico de
// consumo (OCR inconsistente — ver secoes.ts:343, "[\d.]*\d[.,]\d{2}"), então não dá
// pra assumir que '.' é sempre agrupamento de milhar (ex.: "162.00" viraria 16200).
// Usa o ÚLTIMO separador como decimal e descarta os demais.
function parseBrNumber(s?: string): number | undefined {
    if (!s) return undefined;
    const str = String(s).trim();
    const decimalPos = Math.max(str.lastIndexOf(','), str.lastIndexOf('.'));
    const normalized = decimalPos === -1
        ? str
        : `${str.slice(0, decimalPos).replace(/[.,]/g, '')}.${str.slice(decimalPos + 1)}`;
    const n = parseFloat(normalized);
    return isNaN(n) ? undefined : n;
}

/** Converte o resultado da edge function `extrair-fatura-crm` (parser determinístico +
 *  separador de endereço, exclusivo do CRM) no mesmo formato "achatado" que o webhook
 *  n8n/LLM antigo produzia, para reaproveitar `mapFaturaDataToUC` e os consumidores
 *  existentes (EnviarFaturaModal, NovaNegociacaoAutomaticaModal, DetailRightColumn). */
export function mapExtracaoFaturaCrmToFaturaData(resposta: ExtracaoFaturaCrmResposta): Record<string, any> {
    const cab = resposta.estruturado?.cabecalho || {};
    const end = resposta.endereco;

    // Tarifa do consumo (não a de bandeira) fica no item "CONSUMO" da seção FORNECIMENTO.
    // Em faturas com geração distribuída (GD/compensação) não existe uma linha "CONSUMO" simples:
    // a energia é dividida em "CONSUMO NÃO COMPENSADO" (tarifa cheia, é essa que queremos) e
    // "CONSUMO SCEE" (tarifa compensada/reduzida). As duas formas nunca coexistem na mesma fatura,
    // então um único predicado com fallback resolve os dois casos (mesma lógica validada em
    // supabase/functions/_shared/mapeador-faturas/index.ts:86-89).
    const itemConsumo = (resposta.estruturado?.itens || []).find(
        (i) =>
            i.grupo_secao === 'FORNECIMENTO' &&
            (/CONSUMO\s+N[ÃA]O\s+COMPENSADO/i.test(i.descricao || '') ||
                (/^CONSUMO\b/i.test((i.descricao || '').trim()) && !/SCEE/i.test(i.descricao || '')))
    );

    // O parser não emite a linha "MÉDIA" da tabela de histórico como campo próprio
    // (ela não casa com o regex de mês/ano) — a média dos meses do histórico é a
    // aproximação mais fiel disponível.
    const consumos = (resposta.estruturado?.historico || [])
        .map((h) => parseBrNumber(h.consumo_faturado))
        .filter((n): n is number => n !== undefined);
    const consumoMedio = consumos.length
        ? Math.round((consumos.reduce((a, b) => a + b, 0) / consumos.length) * 100) / 100
        : undefined;

    return {
        numero_uc: cab.unidade_consumidora || undefined,
        num_medidor: cab.numero_medidor || undefined,
        concessionaria: 'Equatorial Goiás',
        tipo_ligacao: cab.tipo_fornecimento || undefined,
        grupo_tarifario: cab.sub_grupo || cab.grupo || undefined,
        consumo_medio_kwh: consumoMedio,
        valor_tarifa: itemConsumo ? parseBrNumber(itemConsumo.preco_unit_com_tributos) : undefined,
        titular_fatura: cab.nome_cliente || undefined,
        cpf_cnpj_titular: cab.cpf_cnpj || undefined,
        cep: cab.end_cep || undefined,
        logradouro: end?.logradouro || cab.end_logradouro || undefined,
        numero: end?.numero || undefined,
        complemento: end?.complemento || undefined,
        quadra: end?.quadra || undefined,
        lote: end?.lote || undefined,
        bairro: cab.end_bairro || undefined,
        cidade: cab.end_cidade || undefined,
        estado: cab.end_uf || undefined,
    };
}
